import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { getAllDancerNames, searchDancers, getDancerCompetitions } from './utils/dataCache';
import './CompareDancers.css';

export default function CompareDancers() {
  const [dancers, setDancers] = useState([null, null, null, null]); // Up to 4 dancers
  const [searchTerms, setSearchTerms] = useState(['', '', '', '']);
  const [searchResults, setSearchResults] = useState([[], [], [], []]);
  const [allDancerNames, setAllDancerNames] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(2);

  // Load all dancer names on component mount
  useEffect(() => {
    loadAllDancerNames();
  }, []);

  const loadAllDancerNames = async () => {
    try {
      console.log('🔍 Loading all dancer names from cache...');
      const sortedNames = await getAllDancerNames();
      setAllDancerNames(sortedNames);
      console.log(`✅ Loaded ${sortedNames.length} unique dancer names from cache`);
    } catch (error) {
      console.error('❌ Error loading dancer names:', error);
    }
  };

  const handleSearchChange = (columnIndex, searchTerm) => {
    const newSearchTerms = [...searchTerms];
    newSearchTerms[columnIndex] = searchTerm;
    setSearchTerms(newSearchTerms);

    // Filter dancers based on search term using cached search
    if (searchTerm.trim()) {
      const filtered = searchDancers(searchTerm);
      const newSearchResults = [...searchResults];
      newSearchResults[columnIndex] = filtered;
      setSearchResults(newSearchResults);
    } else {
      const newSearchResults = [...searchResults];
      newSearchResults[columnIndex] = [];
      setSearchResults(newSearchResults);
    }
  };

  const selectDancer = (columnIndex, dancerName) => {
    const newDancers = [...dancers];
    newDancers[columnIndex] = dancerName;
    setDancers(newDancers);

    // Clear search
    const newSearchTerms = [...searchTerms];
    const newSearchResults = [...searchResults];
    newSearchTerms[columnIndex] = '';
    newSearchResults[columnIndex] = [];
    setSearchTerms(newSearchTerms);
    setSearchResults(newSearchResults);
  };

  const removeDancer = (columnIndex) => {
    const newDancers = [...dancers];
    newDancers[columnIndex] = null;
    setDancers(newDancers);
  };

  const addColumn = () => {
    if (visibleColumns < 4) {
      setVisibleColumns(visibleColumns + 1);
    }
  };

  const removeColumn = () => {
    if (visibleColumns > 2) {
      // Remove dancer from the last column
      const newDancers = [...dancers];
      newDancers[visibleColumns - 1] = null;
      setDancers(newDancers);
      setVisibleColumns(visibleColumns - 1);
    }
  };

  // Perform comparison when dancers change
  const performComparison = useCallback(async () => {
    setLoading(true);
    console.log('🔍 Performing dancer comparison...');

    try {
      const activeDancers = dancers.filter(Boolean);
      console.log('👥 Active dancers for comparison:', activeDancers);
      
      if (activeDancers.length < 2) {
        console.log('❌ Need at least 2 dancers for comparison');
        setComparisons([]);
        setLoading(false);
        return;
      }
      
      const competitions = [];

      // Use cached data instead of direct Firestore queries
      console.log('🔍 Getting competitions from cache...');
      
      // Get all relevant competitions for each dancer from cache
      const allRelevantCompetitions = new Map();
      
      for (const dancerName of activeDancers) {
        const dancerCompetitions = getDancerCompetitions(dancerName);
        console.log(`� Found ${dancerCompetitions.length} competitions for ${dancerName}`);
        
        dancerCompetitions.forEach(comp => {
          if (!allRelevantCompetitions.has(comp.id)) {
            allRelevantCompetitions.set(comp.id, comp);
          }
        });
      }

      console.log(`📚 Processing ${allRelevantCompetitions.size} relevant competitions from cache`);

      let relevantCompetitions = 0;
      const addedCompetitions = new Set(); // Track added competitions to prevent duplicates
      
      allRelevantCompetitions.forEach((competition) => {
        // Quick check: does this competition have any of our dancers?
        if (competition.results && Array.isArray(competition.results)) {
          const hasOurDancers = competition.results.some(result => {
            const name = result.dancerName || result.name;
            return activeDancers.includes(name);
          });
          
          if (!hasOurDancers) {
            return; // Skip this competition entirely
          }
          
          relevantCompetitions++;
          console.log('📊 Processing relevant competition:', competition.name, 'with', competition.results.length, 'results');
          
          // Debug competition structure - let's see what fields are actually available
          if (relevantCompetitions === 1) {
            console.log('🔍 DEBUGGING - Competition structure:', {
              competitionKeys: Object.keys(competition),
              category: competition.category,
              discipline: competition.discipline,
              dance: competition.dance,
              danceStyle: competition.danceStyle,
              competitionCategory: competition.competitionCategory,
              ageCategory: competition.ageCategory,
              firstResultSample: competition.results[0] ? Object.keys(competition.results[0]) : 'No results'
            });
          }
          
          // Group by category
          const foundDancers = {};
          const withdrawnCategories = new Set(); // categories where a compared dancer withdrew
          let foundAnyDancer = false;
          
          competition.results.forEach((result, index) => {
            const name = result.dancerName || result.name;
            
            // Use the actual field structure from the data we just saw
            const category = competition.category; // This exists in the data
            const discipline = competition.discipline; // This exists in the data
            const placement = result.place; // Use 'place' from the result data
            
            // Debug first few results to see what we're working with
            if (index < 3) {
              console.log(`  🔍 Sample result ${index}:`, {
                name: name,
                category: category,
                discipline: discipline,
                placement: placement,
                isOurDancer: activeDancers.includes(name),
                resultKeys: Object.keys(result)
              });
            }
            
            if (activeDancers.includes(name)) {
              foundAnyDancer = true;
              
              // Create a meaningful category even if fields are missing
              const categoryKey = category && discipline ? `${category} - ${discipline}` : 
                                 category ? category : 
                                 discipline ? discipline : 
                                 'Unknown Category';
              
              console.log('  ✅ Found dancer:', name, 'in category:', category, 'discipline:', discipline, 'placement:', placement, 'categoryKey:', categoryKey);
              
              // A withdrawn dancer voids the whole competition for the comparison
              if (result.withdrawn || result.withdraw === "true" || result.withdraw === true) {
                withdrawnCategories.add(categoryKey);
              }

              if (!foundDancers[categoryKey]) {
                foundDancers[categoryKey] = [];
              }
              foundDancers[categoryKey].push({
                name,
                placement: placement,
                points: result.points,
                dance: competition.dance || result.dance || discipline || 'Unknown Dance',
                discipline: discipline || 'Unknown Discipline',
                studio: result.studio
              });
            }
          });
          
          if (!foundAnyDancer) {
            console.log('  ❌ No dancers found in this competition despite initial check');
          }

          // Only include categories where at least 2 of our dancers competed
          // If more than 2 dancers selected, only show competitions where ALL dancers competed
          const activeDancerCount = activeDancers.length;
          Object.keys(foundDancers).forEach(category => {
            if (withdrawnCategories.has(category)) {
              console.log('  🚫 Skipping', competition.name, '-', category, '(a compared dancer withdrew)');
              return;
            }
            const competingDancers = foundDancers[category].length;
            const shouldIncludeCompetition = activeDancerCount > 2 
              ? competingDancers === activeDancerCount // All dancers must compete
              : competingDancers >= 2; // At least 2 dancers for 2-dancer comparison

            if (shouldIncludeCompetition) {
              // Create a unique key to prevent duplicates based on competition details
              const competitionKey = `${competition.id}-${category}-${foundDancers[category].map(d => d.name).sort().join(',')}`;
              
              if (!addedCompetitions.has(competitionKey)) {
                console.log('  🎯 Adding head-to-head result for category:', category, 'with', competingDancers, 'dancers');
                competitions.push({
                  id: competition.id,
                  name: competition.name || 'Unnamed Competition',
                  date: competition.date || competition.timestamp,
                  category: category,
                  dance: competition.dance,
                  dancers: foundDancers[category],
                  structure: 'cached'
                });
                addedCompetitions.add(competitionKey);
              } else {
                console.log('  ⏭️ Skipping duplicate competition:', category, 'for', competition.name);
              }
            } else {
              console.log('  ❌ Category', category, 'only has', competingDancers, 'of our dancers, need', activeDancerCount > 2 ? activeDancerCount : '2+');
            }
          });
        }
      });
      
      console.log('📈 Processed', relevantCompetitions, 'relevant competitions from cache');

      // Sort competitions by date (newest first) with better date handling
      competitions.sort((a, b) => {
        // Handle different date formats and ensure newest comes first
        const parseDate = (dateInput) => {
          if (!dateInput) return new Date(0); // Very old date for missing dates
          
          // Handle Date objects
          if (dateInput instanceof Date) {
            return dateInput;
          }
          
          // Handle Firebase Timestamp objects
          if (dateInput && typeof dateInput.toDate === 'function') {
            return dateInput.toDate();
          }
          
          // Handle strings
          if (typeof dateInput === 'string') {
            // Try to parse the date string directly first
            let date = new Date(dateInput);
            
            // Check if the date is valid
            if (!isNaN(date.getTime())) {
              return date;
            }
            
            // If direct parsing failed, try different formats
            const parts = dateInput.split(/[-/.]/);
            if (parts.length === 3) {
              // Try different date format combinations
              const formats = [
                new Date(parts[2], parts[1] - 1, parts[0]), // DD/MM/YYYY or DD-MM-YYYY
                new Date(parts[0], parts[1] - 1, parts[2]), // YYYY/MM/DD or YYYY-MM-DD
                new Date(parts[1], parts[0] - 1, parts[2])  // MM/DD/YYYY or MM-DD-YYYY
              ];
              
              for (const format of formats) {
                if (!isNaN(format.getTime())) {
                  return format;
                }
              }
            }
          }
          
          return new Date(0); // Fallback for unparseable dates
        };
        
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);
        
        // Sort descending (newest first)
        return dateB.getTime() - dateA.getTime();
      });

      console.log('🎯 Final comparison results:', competitions.length, 'competitions found');
      console.log('📋 Sorted competition dates (first 5):');
      competitions.slice(0, 5).forEach((comp, i) => {
        const parsedDate = comp.date ? (() => {
          // Handle both Date objects and strings
          if (comp.date instanceof Date) {
            return comp.date;
          }
          
          // Handle string dates
          if (typeof comp.date === 'string') {
            const parts = comp.date.split(/[-/.]/);
            if (parts.length === 3) {
              // Assuming DD/MM/YYYY format
              return new Date(parts[2], parts[1] - 1, parts[0]);
            }
            return new Date(comp.date);
          }
          
          // Handle Firebase Timestamp objects
          if (comp.date && typeof comp.date.toDate === 'function') {
            return comp.date.toDate();
          }
          
          return new Date(0);
        })() : new Date(0);
        console.log(`${i+1}. ${comp.name} - Date: '${comp.date}' - Parsed: ${parsedDate.toISOString().split('T')[0]}`);
      });
      console.log('📋 Competition details:', competitions.map(c => ({
        name: c.name,
        category: c.category,
        dancerCount: c.dancers.length,
        structure: c.structure
      })));

      setComparisons(competitions);
      console.log(`✅ Found ${competitions.length} competitions where these dancers met`);

      // Log comparison event to Firestore
      if (activeDancers.length >= 2) {
        try {
          const user = auth.currentUser;
          await addDoc(collection(db, 'dancerComparisons'), {
            dancers: activeDancers,
            resultsCount: competitions.length,
            // Compact summary of what the user saw, for the admin Visitor Statistics > Comparisons tab
            competitions: competitions.slice(0, 40).map((c) => {
              const d = c.date && typeof c.date.toDate === 'function' ? c.date.toDate() : c.date;
              return {
                name: c.name || '',
                category: c.category || '',
                date: d instanceof Date && !isNaN(d) ? d.toISOString().slice(0, 10) : (typeof d === 'string' ? d : null),
                placements: (c.dancers || []).map((x) => ({ name: x.name, placement: x.placement ?? null })),
              };
            }),
            userId: user ? user.uid : 'anonymous',
            userEmail: user ? user.email : null,
            userName: user ? (user.displayName || user.email) : null,
            timestamp: new Date(),
          });
        } catch (logErr) {
          // non-blocking — don't surface logging errors to users
        }
      }
    } catch (error) {
      console.error('❌ Error performing comparison:', error);
    } finally {
      setLoading(false);
    }
  }, [dancers]);

  useEffect(() => {
    if (dancers.filter(Boolean).length >= 2) {
      performComparison();
    } else {
      setComparisons([]);
    }
  }, [dancers, performComparison]);

  const getBestPlacement = (dancers) => {
    const placements = dancers.map(d => d.placement).filter(p => p !== undefined && p !== null);
    return placements.length > 0 ? Math.min(...placements) : null;
  };

  const getWorstPlacement = (dancers) => {
    const placements = dancers.map(d => d.placement).filter(p => p !== undefined && p !== null);
    return placements.length > 0 ? Math.max(...placements) : null;
  };

  const formatCategoryDisplay = (category, dance) => {
    // For Latin categories, show the specific dance instead of "Latin Dance"
    if (category && category.toLowerCase().includes('latin') && dance) {
      // Extract the non-Latin part of the category and add the dance
      const baseCategory = category.replace(/latin/gi, '').trim();
      return baseCategory ? `${baseCategory} ${dance}` : dance;
    }
    
    // For Contemporary, Hip Hop, and Commercial, remove repeated words
    if (category) {
      // Remove repeated "Contemporary", "Hip Hop", or "Commercial" from the category
      let formattedCategory = category;
      
      // Pattern to match repeated words (case insensitive)
      const words = formattedCategory.split(/\s+/);
      const uniqueWords = [];
      const seenWords = new Set();
      
      for (const word of words) {
        const lowerWord = word.toLowerCase();
        if (!seenWords.has(lowerWord)) {
          uniqueWords.push(word);
          seenWords.add(lowerWord);
        }
      }
      
      formattedCategory = uniqueWords.join(' ');
      return formattedCategory;
    }
    
    return category || 'Unknown Category';
  };

  const formatTimestampToDate = (timestampData) => {
    if (!timestampData || !timestampData.seconds) return null;
    
    const date = new Date(timestampData.seconds * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateHeadToHeadStats = (comparisons, activeDancers) => {
    const stats = {};
    const matchups = {};
    
    // Initialize stats for each dancer
    activeDancers.forEach(dancer => {
      stats[dancer] = {
        wins: 0,
        totalCompetitions: 0,
        averagePosition: 0,
        positionSum: 0
      };
    });

    // Initialize matchup matrix
    activeDancers.forEach(dancer1 => {
      matchups[dancer1] = {};
      activeDancers.forEach(dancer2 => {
        if (dancer1 !== dancer2) {
          matchups[dancer1][dancer2] = { wins: 0, total: 0 };
        }
      });
    });

    // Calculate stats from competitions
    comparisons.forEach(competition => {
      // Ensure dancers is an array before filtering
      if (!competition.dancers || !Array.isArray(competition.dancers)) {
        console.warn('❌ Competition has invalid dancers data:', {
          name: competition.name,
          id: competition.id,
          dancersType: typeof competition.dancers,
          dancersValue: competition.dancers,
          structure: competition.structure
        });
        return;
      }
      
      const competingDancers = competition.dancers.filter(d => activeDancers.includes(d.name));
      if (competingDancers.length < 2) return;

      // Sort by placement (lower is better)
      competingDancers.sort((a, b) => {
        const aPlace = a.placement !== null && a.placement !== undefined ? a.placement : 999;
        const bPlace = b.placement !== null && b.placement !== undefined ? b.placement : 999;
        return aPlace - bPlace;
      });

      // Find the best placement (lowest number)
      const bestPlacement = Math.min(...competingDancers
        .filter(d => d.placement !== null && d.placement !== undefined)
        .map(d => d.placement));

      // Update overall stats
      competingDancers.forEach((dancer, index) => {
        stats[dancer.name].totalCompetitions++;
        if (dancer.placement !== null && dancer.placement !== undefined) {
          stats[dancer.name].positionSum += dancer.placement;
        }
        // Only count as a win if dancer has the best placement (handles ties properly)
        if (dancer.placement === bestPlacement) {
          stats[dancer.name].wins++;
        }
      });

      // Update head-to-head matchups
      for (let i = 0; i < competingDancers.length; i++) {
        for (let j = 0; j < competingDancers.length; j++) {
          if (i !== j) {
            const dancer1 = competingDancers[i];
            const dancer2 = competingDancers[j];
            matchups[dancer1.name][dancer2.name].total++;
            
            // Compare actual placements instead of indices
            const place1 = dancer1.placement !== null && dancer1.placement !== undefined ? dancer1.placement : 999;
            const place2 = dancer2.placement !== null && dancer2.placement !== undefined ? dancer2.placement : 999;
            
            // Only count as a win if dancer1 placed BETTER (lower) than dancer2 (no tie)
            if (place1 < place2) {
              matchups[dancer1.name][dancer2.name].wins++;
            }
          }
        }
      }
    });

    // Calculate averages
    Object.keys(stats).forEach(dancer => {
      if (stats[dancer].totalCompetitions > 0) {
        stats[dancer].averagePosition = stats[dancer].positionSum / stats[dancer].totalCompetitions;
      }
    });

    return { stats, matchups };
  };

  const getPlacementColor = (dancer, allDancers) => {
    const bestPlacement = getBestPlacement(allDancers);
    const worstPlacement = getWorstPlacement(allDancers);
    
    if (dancer.placement === bestPlacement && dancer.placement !== worstPlacement) {
      return '#4CAF50'; // Green for best
    } else if (dancer.placement === worstPlacement && dancer.placement !== bestPlacement) {
      return '#f44336'; // Red for worst
    }
    return '#333'; // Default color
  };

  const formatDisplayDate = (dateInput) => {
    if (!dateInput) return 'Unknown Date';
    
    // Handle Date objects
    if (dateInput instanceof Date) {
      return dateInput.toLocaleDateString();
    }
    
    // Handle Firebase Timestamp objects
    if (dateInput && typeof dateInput.toDate === 'function') {
      return dateInput.toDate().toLocaleDateString();
    }
    
    // Handle strings
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      return dateInput; // Return as-is if it's already a formatted string
    }
    
    return 'Unknown Date';
  };

  return (
    <div className="compare-dancers-container" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
        🏆 Compare Dancers
      </h1>

      {/* Column Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '20px', 
        gap: '10px' 
      }}>
        <button
          onClick={addColumn}
          disabled={visibleColumns >= 4}
          style={{
            padding: '8px 16px',
            backgroundColor: visibleColumns < 4 ? '#4CAF50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: visibleColumns < 4 ? 'pointer' : 'not-allowed'
          }}
        >
          ➕ Add Dancer Column
        </button>
        
        <button
          onClick={removeColumn}
          disabled={visibleColumns <= 2}
          style={{
            padding: '8px 16px',
            backgroundColor: visibleColumns > 2 ? '#f44336' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: visibleColumns > 2 ? 'pointer' : 'not-allowed'
          }}
        >
          ➖ Remove Column
        </button>
      </div>

      {/* Dancer Selection Columns */}
      <div className={`dancer-selection-grid columns-${visibleColumns}`} style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${visibleColumns}, 1fr)`, 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        {Array.from({ length: visibleColumns }, (_, index) => (
          <div key={index} className="dancer-card" style={{ 
            border: '2px solid #ddd', 
            borderRadius: '8px', 
            padding: '15px',
            backgroundColor: 'white'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#555' }}>
              Dancer {index + 1}
            </h3>

            {dancers[index] ? (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#e8f5e8', 
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                  {dancers[index]}
                </span>
                <button
                  onClick={() => removeDancer(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#f44336',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Search for a dancer..."
                  value={searchTerms[index]}
                  onChange={(e) => handleSearchChange(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '10px'
                  }}
                />

                {searchResults[index].length > 0 && (
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}>
                    {searchResults[index].map((name, nameIndex) => (
                      <div
                        key={nameIndex}
                        onClick={() => selectDancer(index, name)}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          borderBottom: nameIndex < searchResults[index].length - 1 ? '1px solid #eee' : 'none',
                          backgroundColor: 'white'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comparison Results */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          🔄 Analyzing competitions where these dancers met...
        </div>
      )}

      {!loading && comparisons.length > 0 && (
        <div>
          <h2 style={{ color: '#333', marginBottom: '20px' }}>
            📊 Head-to-Head Results ({comparisons.length} competitions)
          </h2>

          {/* Head-to-Head Statistics Summary */}
          {(() => {
            const activeDancers = dancers.filter(Boolean);
            const { stats, matchups } = calculateHeadToHeadStats(comparisons, activeDancers);
            
            return (
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '20px', 
                marginBottom: '25px' 
              }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#333', textAlign: 'center' }}>
                  🏆 Overall Statistics
                </h3>
                
                <div className="dancer-stats-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `repeat(${activeDancers.length}, 1fr)`, 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  {activeDancers.map((dancer, index) => {
                    // Find the best performer for crown display (without mutating the original array)
                    const winCounts = activeDancers.map(d => ({ dancer: d, wins: stats[d].wins }));
                    const topPerformer = winCounts.reduce((best, current) => 
                      current.wins > best.wins ? current : best
                    );
                    const isTopPerformer = dancer === topPerformer.dancer;
                    
                    return (
                      <div key={dancer} style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: isTopPerformer ? '2px solid #4CAF50' : '1px solid #ddd'
                      }}>
                        <div style={{ 
                          fontSize: '1.1rem', 
                          fontWeight: 'bold',
                          color: isTopPerformer ? '#4CAF50' : '#333',
                          textAlign: 'center',
                          marginBottom: '8px'
                        }}>
                          {isTopPerformer ? '👑 ' : ''}{dancer}
                        </div>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          <strong>{stats[dancer].wins}</strong> wins out of <strong>{stats[dancer].totalCompetitions}</strong>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="cmp-list">
            {comparisons.map((competition, index) => {
              const selected = dancers.filter(Boolean);
              const compDancers = Array.isArray(competition.dancers) ? competition.dancers : null;
              if (!compDancers) return null;

              return (
                <div key={index} className="cmp-row">
                  <div className="cmp-info">
                    <div className="cmp-name" title={competition.name}>{competition.name}</div>
                    <div className="cmp-meta">
                      <span>{formatDisplayDate(competition.date)}</span>
                      <span className="cmp-cat">{formatCategoryDisplay(competition.category, competition.dance)}</span>
                    </div>
                  </div>

                  <div className="cmp-versus">
                    {selected.map((selectedDancer, i) => {
                      const dancerResult = compDancers.find(d => d.name === selectedDancer);
                      const color = dancerResult ? getPlacementColor(dancerResult, compDancers) : null;
                      const state = !dancerResult ? 'dnc' : color === '#4CAF50' ? 'best' : color === '#f44336' ? 'worst' : '';
                      const place = dancerResult
                        ? (dancerResult.placement !== null && dancerResult.placement !== undefined ? dancerResult.placement : 'N/A')
                        : '—';

                      return (
                        <React.Fragment key={i}>
                          <div className={`cmp-slot ${state}`} title={dancerResult ? undefined : 'Did not compete'}>
                            <span className="cmp-place">{place}</span>
                            <span className="cmp-dancer">{selectedDancer}</span>
                          </div>
                          {i < selected.length - 1 && <span className="cmp-vs">VS</span>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && dancers.filter(Boolean).length >= 2 && comparisons.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '30px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <h3 style={{ color: '#856404' }}>No Head-to-Head Meetings Found</h3>
          <p style={{ color: '#856404' }}>
            These dancers have not competed against each other in any competition category.
          </p>
        </div>
      )}

      {dancers.filter(Boolean).length < 2 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '30px',
          backgroundColor: '#e2e3e5',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <h3 style={{ color: '#6c757d' }}>Select at least 2 dancers to compare</h3>
          <p style={{ color: '#6c757d' }}>
            Search for dancers in the columns above to see their head-to-head results.
          </p>
        </div>
      )}
    </div>
  );
}
